import { FC, useMemo } from 'react';
import { Controller, FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, FormInputText, InputLabel, Modal, Select, Text } from '@hopskipdrive/react-components';

import { Button } from '../../../shared/components/Button';
import { PhotoOrInitials } from '../../../shared/components/PhotoOrInitials';
import { colorPalette } from '../../../shared/components/variables';
import { CancellationReason, RemoveRidersPayload, RiderOnRide } from '../../types/rides';

import { riderRemovalsValidationSchema } from './riderRemovalsValidationSchema';

interface RiderRemovalsModalProps {
  ridersToRemove: RiderOnRide[];
  reasons: CancellationReason[];
  isPending: boolean;
  onClose: () => void;
  onRemove: (formData: RemoveRidersPayload) => void;
  isEditRide?: boolean;
}

interface FormData {
  removeRider: {
    riderId: number;
    type: string;
    authorizerDescription: string;
    additionalDetails: string;
  }[];
}

const defaultRiderRemoval = {
  riderId: 0,
  type: '',
  authorizerDescription: '',
  additionalDetails: '',
};

export const RiderRemovalsModal: FC<RiderRemovalsModalProps> = ({
  onClose,
  reasons,
  ridersToRemove,
  isPending,
  onRemove,
  isEditRide,
}) => {
  const formattedRiders = ridersToRemove.map((rider) => ({
    ...defaultRiderRemoval,
    riderId: rider.id,
  }));

  const methods = useForm<FormData>({
    defaultValues: useMemo(() => {
      return { removeRider: formattedRiders };
    }, [formattedRiders]),
    resolver: yupResolver(riderRemovalsValidationSchema()),
  });

  return (
    <Modal open={true} maxWidth="md" data-testid="edit-organizer-info">
      {isEditRide ? (
        <Modal.Title>The edit will remove the following Rider(s) from the ride.</Modal.Title>
      ) : (
        <Modal.Title>Remove the following Rider from the ride</Modal.Title>
      )}

      <Modal.Content>
        <FormProvider {...methods}>
          <RiderRemovalsForm reasons={reasons} ridersToRemove={ridersToRemove} handleSubmit={onRemove} />
        </FormProvider>
      </Modal.Content>
      <Modal.Actions style={{ borderTop: `1px solid ${colorPalette.neutralVariant90}` }}>
        <Button variant="text" onClick={onClose} disabled={isPending}>
          Never mind
        </Button>
        <Button type="submit" color="primary" form="rider-removals-form" loading={isPending}>
          {isPending ? 'Removing...' : 'Remove Rider(s)'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

interface RiderRemovalsFormProps {
  ridersToRemove: RiderOnRide[];
  handleSubmit: (formData: FormData) => void;
  reasons: CancellationReason[];
}

const RiderRemovalsForm: FC<RiderRemovalsFormProps> = ({ ridersToRemove, handleSubmit, reasons }) => {
  const methods = useFormContext<FormData>();
  const {
    control,
    register,
    formState: { errors },
  } = methods;

  const { fields } = useFieldArray({
    control,
    name: 'removeRider',
  });

  return (
    <form
      id="rider-removals-form"
      onSubmit={methods.handleSubmit(handleSubmit)}
      style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
    >
      {fields.map((item, index) => {
        const values = ridersToRemove[index];

        return (
          <div key={`riderRemovals-${item.id}`}>
            <PhotoOrInitials
              key={`riderRemovals-rider-${values.id}`}
              fullName={`${values.firstName} ${values.lastName}`}
              pictureUrl={values.profilePictureUrl}
              size={33}
              fontSize={14}
            />
            <Text variant="h6">{`Rider: ${values.firstName} ${values.lastName}`}</Text>
            <Box display="flex" gap="6px" flexDirection="column" mt="6px">
              <FormFieldContent>
                <InputLabel fieldtype="required" htmlFor="removalReason">
                  Reason
                </InputLabel>
                <Controller
                  control={control}
                  name={`removeRider.${index}.type`}
                  render={({ field: { value, onChange } }) => (
                    <Select
                      options={reasons}
                      value={value}
                      setValue={onChange}
                      style={{ width: '323px' }}
                      renderInput={(params) => (
                        <FormInputText
                          {...params}
                          variant="outlined"
                          data-testid="removalReason"
                          placeholder="Select"
                          error={!!errors.removeRider && !!errors.removeRider[index]?.type}
                          helperText={
                            // @ts-ignore
                            // type is a native key for Yup and is conflicting
                            errors?.removeRider?.[index]?.type && errors?.removeRider?.[index]?.type?.message
                          }
                        />
                      )}
                    />
                  )}
                />
              </FormFieldContent>
              <FormFieldContent>
                <InputLabel fieldtype="required" htmlFor="removalReason">
                  Requested by
                </InputLabel>
                <Controller
                  control={control}
                  name={`removeRider.${index}.authorizerDescription`}
                  render={({ field: { value, onChange } }) => (
                    <FormInputText
                      value={value}
                      onChange={onChange}
                      variant="outlined"
                      data-testid="authorizerDescription"
                      style={{ width: '323px' }}
                      placeholder="Enter name"
                      error={!!(errors.removeRider && errors.removeRider[index]?.authorizerDescription)}
                      helperText={errors.removeRider && errors.removeRider[index]?.authorizerDescription?.message}
                    />
                  )}
                />
              </FormFieldContent>
              <FormFieldContent>
                <InputLabel htmlFor="additionalDetails">Note</InputLabel>
                <Controller
                  control={control}
                  name={`removeRider.${index}.additionalDetails`}
                  render={({ field: { value, onChange } }) => (
                    <FormInputText
                      value={value}
                      onChange={onChange}
                      variant="outlined"
                      data-testid="additionalDetails"
                      style={{ width: '323px' }}
                      placeholder="Enter any additional info"
                    />
                  )}
                />
              </FormFieldContent>
            </Box>
            <input
              type="hidden"
              {...register(`removeRider.${index}.riderId`, {
                value: values?.id,
              })}
            />
          </div>
        );
      })}
    </form>
  );
};

const FormFieldContent = ({ children }) => (
  <Box display="flex" alignItems="center" justifyContent="space-between">
    {children}
  </Box>
);
